import React, { useMemo, useState } from 'react';
import InfoTip from '../../../components/InfoTip';
import { fmtMt, fmtNb, fmtDateFr } from '../../pointJour/format';

// Composants partagés du dashboard technique : liens fiches, chiffre cliquable, tableau triable, formats.

export const BROWN = '#6d4e36';
export const fmtL = (v) => `${fmtNb(v)} L`;
export const fmtH = (v) => `${fmtNb(v)} h`;
export const fmtDH = (v) => `${fmtMt(v)} DH`;
export { fmtMt, fmtNb, fmtDateFr };

// Lien vers la fiche d'une entité (nouvel onglet, période du dashboard conservée)
export const ficheUrl = (type, id, periode) => {
  const q = periode ? `?start=${periode.start}&end=${periode.end}` : '';
  return `/fiche/${type}/${id}${q}`;
};
export const FicheLien = ({ type, id, periode, children }) => (id
  ? <a href={ficheUrl(type, id, periode)} target="_blank" rel="noreferrer" title="Ouvrir la fiche">{children}</a>
  : <span>{children}</span>);

// Chiffre cliquable (ouvre une liste) ; inerte sans onClick
export const Clic = ({ onClick, children, style }) => (onClick
  ? <span role="button" tabIndex={0} onClick={onClick} onKeyDown={e => e.key === 'Enter' && onClick()}
      title="Voir la liste" style={{ cursor: 'pointer', borderBottom: '1px dotted currentColor', ...style }}>{children}</span>
  : <span style={style}>{children}</span>);

export const Section = ({ titre, info, children, actions }) => (
  <div className="mb-4">
    <div className="d-flex justify-content-between align-items-center mb-2" style={{ borderBottom: '2px solid #f5f5db', paddingBottom: 4 }}>
      <h6 style={{ color: BROWN, fontWeight: 700, margin: 0 }}>{titre} {info && <InfoTip text={info} />}</h6>
      {actions}
    </div>
    {children}
  </div>
);

// Petite carte statistique (cliquable)
export const Stat = ({ label, value, sub, alert, info, onClick }) => (
  <div className="col-6 col-md mb-2">
    <div style={{ background: alert ? '#fdecea' : '#f9f6ef', borderRadius: 8, padding: '8px 12px', height: '100%' }}>
      <div style={{ fontSize: '0.75rem', color: BROWN }}>{label} {info && <InfoTip text={info} />}</div>
      <div style={{ fontSize: '1.15rem', fontWeight: 700, color: alert ? '#c62828' : BROWN }}><Clic onClick={onClick}>{value}</Clic></div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#777' }}>{sub}</div>}
    </div>
  </div>
);

/* Tableau triable.
   columns : [{ key, label, num, fmt(v, row), render(row), total, info, align }]
   onRowClick / ligne cliquable optionnelle ; total = ligne de total pour les colonnes `total`. */
export const Tableau = ({ columns, rows, triInitial, total = false, maxHeight, vide = 'Aucune ligne', small = true }) => {
  const [tri, setTri] = useState(triInitial || null); // { key, dir }
  const tries = useMemo(() => {
    if (!tri) return rows;
    const col = columns.find(c => c.key === tri.key);
    return [...rows].sort((a, b) => {
      const va = col?.sortValue ? col.sortValue(a) : a[tri.key];
      const vb = col?.sortValue ? col.sortValue(b) : b[tri.key];
      const r = typeof va === 'number' || typeof vb === 'number'
        ? (Number(va) || 0) - (Number(vb) || 0)
        : String(va ?? '').localeCompare(String(vb ?? ''), 'fr');
      return tri.dir === 'asc' ? r : -r;
    });
  }, [rows, tri, columns]);
  const trier = (key) => setTri(t => (t?.key === key ? { key, dir: t.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  const totaux = total ? Object.fromEntries(columns.filter(c => c.total).map(c => [c.key, rows.reduce((s, r) => s + (Number(r[c.key]) || 0), 0)])) : null;

  return (
    <div className="table-responsive" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
      <table className={`table table-bordered table-hover ${small ? 'table-sm' : ''} mb-0`} style={{ fontSize: '0.8rem' }}>
        <thead style={{ position: maxHeight ? 'sticky' : undefined, top: 0, zIndex: 1 }}>
          <tr>
            {columns.map(c => (
              <th key={c.key} onClick={() => trier(c.key)} style={{ cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                {c.label}{c.info && <> <InfoTip text={c.info} /></>}
                {tri?.key === c.key && (tri.dir === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tries.length === 0 && <tr><td colSpan={columns.length} className="text-muted">{vide}</td></tr>}
          {tries.map((r, i) => (
            <tr key={r.id ?? r.materielId ?? r.ouvrierId ?? r.chantierId ?? r.citerneId ?? i}>
              {columns.map(c => (
                <td key={c.key} style={{ textAlign: c.align || (c.num ? 'right' : 'left'), whiteSpace: c.num ? 'nowrap' : undefined }}>
                  {c.render ? c.render(r) : c.fmt ? c.fmt(r[c.key], r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {totaux && tries.length > 0 && (
          <tfoot>
            <tr style={{ fontWeight: 700, background: '#f5f5db' }}>
              {columns.map((c, i) => (
                <td key={c.key} style={{ textAlign: c.num ? 'right' : 'left' }}>
                  {i === 0 ? 'Total' : c.total ? (c.fmt ? c.fmt(totaux[c.key], {}) : fmtNb(totaux[c.key])) : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

// Pastille de statut (vidange / papiers)
const STATUTS = {
  DEPASSE: ['#b71c1c', '🔴 Dépassé'], CRITIQUE: ['#e65100', '🟠 Critique'], ALERTE: ['#c87800', '🟡 Alerte'], OK: ['#2e7d32', '🟢 OK'],
};
export const Statut = ({ s }) => (s ? <span style={{ color: STATUTS[s]?.[0], fontWeight: 600, whiteSpace: 'nowrap' }}>{STATUTS[s]?.[1] || s}</span> : <span className="text-muted">—</span>);
